import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  CalendarDaysIcon,
  HomeModernIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';

interface AccommodationType {
  id: string;
  name: string;
  description: string;
  cost_per_night: number;
  currency: string;
  sort_order: number;
  is_active: boolean;
}

interface InviteDates {
  openDate: string;
  closeDate: string;
}

const CURRENCIES = ['USD', 'EUR', 'GBP', 'NGN', 'ZAR', 'KES', 'GHS'];

const emptyAccom: Omit<AccommodationType, 'id'> = {
  name: '',
  description: '',
  cost_per_night: 0,
  currency: 'USD',
  sort_order: 0,
  is_active: true,
};

export default function InviteSettingsPanel() {
  const [dates, setDates] = useState<InviteDates>({ openDate: '', closeDate: '' });
  const [savingDates, setSavingDates] = useState(false);
  const [accommodations, setAccommodations] = useState<AccommodationType[]>([]);
  const [loadingAccom, setLoadingAccom] = useState(true);
  const [editingAccom, setEditingAccom] = useState<AccommodationType | null>(null);
  const [newAccom, setNewAccom] = useState<Omit<AccommodationType, 'id'> | null>(null);
  const [savingAccom, setSavingAccom] = useState(false);

  useEffect(() => {
    fetchDates();
    fetchAccommodations();
  }, []);

  const fetchDates = async () => {
    const { data } = await supabase
      .from('system_settings')
      .select('key, value')
      .in('key', ['interest_form_open_date', 'interest_form_close_date']);
    if (data) {
      const map: Record<string, string> = {};
      data.forEach(row => {
        const raw = row.value;
        map[row.key] = typeof raw === 'string' ? raw.replace(/^"|"$/g, '') : String(raw);
      });
      setDates({
        openDate: map['interest_form_open_date'] || '',
        closeDate: map['interest_form_close_date'] || '',
      });
    }
  };

  const saveDates = async () => {
    setSavingDates(true);
    try {
      const upserts = [
        { key: 'interest_form_open_date', value: dates.openDate, category: 'invitation', is_public: false },
        { key: 'interest_form_close_date', value: dates.closeDate, category: 'invitation', is_public: false },
      ];
      for (const u of upserts) {
        const { error } = await supabase.from('system_settings').upsert(u, { onConflict: 'key' });
        if (error) throw error;
      }
      toast.success('Dates saved');
    } catch {
      toast.error('Failed to save dates');
    } finally {
      setSavingDates(false);
    }
  };

  const fetchAccommodations = async () => {
    setLoadingAccom(true);
    try {
      const { data, error } = await supabase
        .from('invitation_accommodation_types')
        .select('*')
        .order('sort_order', { ascending: true });
      if (error) throw error;
      setAccommodations(data || []);
    } catch {
      toast.error('Failed to load accommodation types');
    } finally {
      setLoadingAccom(false);
    }
  };

  const saveNewAccom = async () => {
    if (!newAccom || !newAccom.name.trim()) return;
    setSavingAccom(true);
    try {
      const { error } = await supabase.from('invitation_accommodation_types').insert({
        ...newAccom,
        sort_order: accommodations.length,
      });
      if (error) throw error;
      toast.success('Room type added');
      setNewAccom(null);
      fetchAccommodations();
    } catch {
      toast.error('Failed to add room type');
    } finally {
      setSavingAccom(false);
    }
  };

  const saveEditAccom = async () => {
    if (!editingAccom || !editingAccom.name.trim()) return;
    setSavingAccom(true);
    try {
      const { error } = await supabase
        .from('invitation_accommodation_types')
        .update({
          name: editingAccom.name,
          description: editingAccom.description,
          cost_per_night: editingAccom.cost_per_night,
          currency: editingAccom.currency,
          sort_order: editingAccom.sort_order,
          is_active: editingAccom.is_active,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingAccom.id);
      if (error) throw error;
      toast.success('Room type updated');
      setEditingAccom(null);
      fetchAccommodations();
    } catch {
      toast.error('Failed to update room type');
    } finally {
      setSavingAccom(false);
    }
  };

  const deleteAccom = async (id: string) => {
    if (!confirm('Delete this room type?')) return;
    try {
      const { error } = await supabase.from('invitation_accommodation_types').delete().eq('id', id);
      if (error) throw error;
      toast.success('Room type deleted');
      setAccommodations(prev => prev.filter(a => a.id !== id));
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-gray-200">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-gray-50 rounded-t-2xl">
          <CalendarDaysIcon className="w-5 h-5 text-golf-orange" />
          <h2 className="font-semibold text-golf-gray text-sm">Invitation Window</h2>
        </div>
        <div className="p-6">
          <p className="text-sm text-gray-500 mb-5">Set when the invite form opens and closes. Leave blank for no restriction.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Open Date</label>
              <input
                type="datetime-local"
                value={dates.openDate}
                onChange={e => setDates(prev => ({ ...prev, openDate: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-300 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange [color-scheme:light]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Close Date (RSVP deadline)</label>
              <input
                type="datetime-local"
                value={dates.closeDate}
                onChange={e => setDates(prev => ({ ...prev, closeDate: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-300 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange [color-scheme:light]"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={saveDates}
              disabled={savingDates}
              className="flex items-center gap-2 px-5 py-2.5 bg-golf-orange text-white text-sm font-semibold rounded-xl hover:bg-golf-orange/90 disabled:opacity-60 transition-colors"
            >
              {savingDates ? <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <CheckIcon className="w-4 h-4" />}
              Save Dates
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <HomeModernIcon className="w-5 h-5 text-golf-orange" />
            <h2 className="font-semibold text-golf-gray text-sm">Accommodation Types</h2>
          </div>
          {!newAccom && (
            <button
              onClick={() => setNewAccom({ ...emptyAccom })}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-golf-orange border border-golf-orange/30 rounded-lg hover:bg-golf-orange/5 transition-colors"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              Add Room Type
            </button>
          )}
        </div>

        <div className="p-6 space-y-3">
          <p className="text-sm text-gray-500 mb-4">These room types appear on the invite form. Guests will see the name, description, and cost per night.</p>

          {newAccom && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="border-2 border-golf-orange/30 rounded-xl p-4 bg-golf-orange/5 space-y-3"
            >
              <p className="text-xs font-semibold text-golf-orange mb-2">New Room Type</p>
              <AccomForm value={newAccom} onChange={setNewAccom as (v: AccommodationType | Omit<AccommodationType, 'id'>) => void} />
              <div className="flex gap-2 pt-1">
                <button
                  onClick={saveNewAccom}
                  disabled={savingAccom}
                  className="flex items-center gap-1.5 px-4 py-2 bg-golf-orange text-white text-sm font-semibold rounded-lg hover:bg-golf-orange/90 disabled:opacity-60 transition-colors"
                >
                  {savingAccom ? <span className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" /> : <CheckIcon className="w-3.5 h-3.5" />}
                  Save
                </button>
                <button onClick={() => setNewAccom(null)} className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                  <XMarkIcon className="w-3.5 h-3.5" />
                  Cancel
                </button>
              </div>
            </motion.div>
          )}

          {loadingAccom ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-3 border-golf-orange border-t-transparent" />
            </div>
          ) : accommodations.length === 0 && !newAccom ? (
            <div className="text-center py-8 text-gray-400">
              <HomeModernIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No room types yet. Add one to show options on the invite form.</p>
            </div>
          ) : (
            accommodations.map(accom => (
              editingAccom?.id === accom.id ? (
                <motion.div
                  key={accom.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="border-2 border-golf-orange/30 rounded-xl p-4 bg-golf-orange/5 space-y-3"
                >
                  <AccomForm value={editingAccom} onChange={setEditingAccom as (v: AccommodationType | Omit<AccommodationType, 'id'>) => void} />
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={saveEditAccom}
                      disabled={savingAccom}
                      className="flex items-center gap-1.5 px-4 py-2 bg-golf-orange text-white text-sm font-semibold rounded-lg hover:bg-golf-orange/90 disabled:opacity-60 transition-colors"
                    >
                      {savingAccom ? <span className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" /> : <CheckIcon className="w-3.5 h-3.5" />}
                      Save Changes
                    </button>
                    <button onClick={() => setEditingAccom(null)} className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                      <XMarkIcon className="w-3.5 h-3.5" />
                      Cancel
                    </button>
                  </div>
                </motion.div>
              ) : (
                <div key={accom.id} className={`flex items-start justify-between gap-4 p-4 rounded-xl border transition-colors ${accom.is_active ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-golf-gray">{accom.name}</span>
                      {accom.cost_per_night > 0 && (
                        <span className="flex items-center gap-0.5 text-xs text-gray-500 font-medium">
                          <CurrencyDollarIcon className="w-3 h-3" />
                          {accom.currency} {accom.cost_per_night.toLocaleString()}/night
                        </span>
                      )}
                      {!accom.is_active && <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Hidden</span>}
                    </div>
                    {accom.description && <p className="text-xs text-gray-500 mt-0.5 truncate">{accom.description}</p>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => setEditingAccom(accom)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-golf-orange transition-colors">
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button onClick={() => deleteAccom(accom.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors">
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function AccomForm({ value, onChange }: {
  value: AccommodationType | Omit<AccommodationType, 'id'>;
  onChange: (v: AccommodationType | Omit<AccommodationType, 'id'>) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Room Name</label>
          <input
            value={value.name}
            onChange={e => onChange({ ...value, name: e.target.value })}
            placeholder="e.g. Deluxe Room"
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-golf-orange"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Cost / Night</label>
            <input
              type="number"
              min={0}
              value={value.cost_per_night}
              onChange={e => onChange({ ...value, cost_per_night: Number(e.target.value) })}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-golf-orange"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Currency</label>
            <select
              value={value.currency}
              onChange={e => onChange({ ...value, currency: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-golf-orange bg-white [color-scheme:light]"
            >
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
        <textarea
          value={value.description}
          onChange={e => onChange({ ...value, description: e.target.value })}
          rows={2}
          placeholder="Brief description shown to guests..."
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-golf-orange resize-none"
        />
      </div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="is_active"
          checked={value.is_active}
          onChange={e => onChange({ ...value, is_active: e.target.checked })}
          className="accent-golf-orange"
        />
        <label htmlFor="is_active" className="text-xs text-gray-600 font-medium">Show on invite form</label>
      </div>
    </div>
  );
}
